/**
 * Editing the Chats side column in place (ADR 0024 §3): the ✎ button in the
 * column's top right corner swaps the blocks for this list. Blocks on the
 * column can be dragged (or moved with the arrows) within and between Top
 * and the Dock, or removed; removed blocks and widgets not yet added wait
 * under "Add widgets" (drag one onto the column to add it, drag a block
 * there to remove it). Every change is saved at once to the person's
 * Workspace settings, the same arrangement Settings → Styling edits.
 *
 * Dragging uses pointer events, not HTML5 drag and drop: it works the same
 * with a mouse, a pen or a finger, whatever is under the pointer.
 */
import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../components/FeatherIcon'
import { addBlock, placeBlock, removeBlock, type SideArea, type SideBlock, type SideBlockId } from './sideLayout'
import { inApp } from '../theme/uiScale'

const BLOCK_ICONS: Record<SideBlockId, string> = {
  overview: 'user',
  spending: 'dollar-sign',
  spendAcross: 'layers',
  lists: 'list',
  notes: 'sticky-note',
  aiReasoning: 'sparkles',
  aiSummary: 'bot',
  onlineSpenders: 'radio',
  activity: 'activity',
  feed: 'zap',
  active: 'messages-square',
}

const AREAS: SideArea[] = ['top', 'dock']
/** Pixels the pointer must travel before a press becomes a drag (a click stays a click). */
const DRAG_SLOP = 4

interface Props {
  blocks: SideBlock[]
  /** Blocks this person can add (Workspace widgets need Workspace). */
  available: (id: SideBlockId) => boolean
  onChange: (next: SideBlock[] | null) => void
}

/** Where a drag would land: an index among the other shown blocks of an area, or back under "Add widgets". */
type Drop = { area: SideArea; index: number } | { area: 'add' }

interface Drag {
  id: SideBlockId
  x: number
  y: number
}

/** The drop under (x, y): the list the pointer is over, and the gap nearest it. */
function dropAt(root: HTMLElement, id: SideBlockId, x: number, y: number): Drop | null {
  for (const list of root.querySelectorAll<HTMLElement>('[data-drop-area]')) {
    const box = list.getBoundingClientRect()
    if (x < box.left - 24 || x > box.right + 24 || y < box.top - 12 || y > box.bottom + 12) continue
    const area = list.dataset.dropArea as SideArea | 'add'
    if (area === 'add') return { area }
    const rows = [...list.querySelectorAll<HTMLElement>('[data-block]')].filter((r) => r.dataset.block !== id)
    const index = rows.filter((r) => {
      const b = r.getBoundingClientRect()
      return y > b.top + b.height / 2
    }).length
    return { area, index }
  }
  return null
}

export function SideEditor({ blocks, available, onChange }: Props) {
  const { t } = useTranslation('chat')
  const root = useRef<HTMLDivElement>(null)
  const press = useRef<{ id: SideBlockId; x: number; y: number } | null>(null)
  const [drag, setDrag] = useState<Drag | null>(null)
  const [drop, setDrop] = useState<Drop | null>(null)
  const name = (id: SideBlockId) => t(`side.blocks.${id}`)
  const onColumn = (area: SideArea) => blocks.filter((b) => b.shown && b.area === area && available(b.id))
  const toAdd = blocks.filter((b) => !b.shown && available(b.id))

  // The latest arrangement for the window listeners below.
  const latest = useRef({ blocks, onChange, drop })
  latest.current = { blocks, onChange, drop }

  useEffect(() => {
    const move = (e: PointerEvent) => {
      const p = press.current
      if (!p || !root.current) return
      if (!drag && Math.hypot(e.clientX - p.x, e.clientY - p.y) < DRAG_SLOP) return
      e.preventDefault()
      setDrag({ id: p.id, x: e.clientX, y: e.clientY })
      setDrop(dropAt(root.current, p.id, e.clientX, e.clientY))
    }
    const up = () => {
      const p = press.current
      press.current = null
      const { blocks: now, onChange: save, drop: target } = latest.current
      if (p && drag && target) {
        const block = now.find((b) => b.id === p.id)
        if (target.area === 'add') {
          if (block?.shown) save(removeBlock(now, p.id))
        } else save(placeBlock(now, p.id, target.area, target.index))
      }
      setDrag(null)
      setDrop(null)
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', up)
    return () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      window.removeEventListener('pointercancel', up)
    }
  }, [drag])

  // A press on a row (not on its buttons) may become a drag.
  const startPress = (id: SideBlockId) => (e: ReactPointerEvent) => {
    if (e.button !== 0 || (e.target as HTMLElement).closest('button')) return
    e.preventDefault()
    press.current = { id, x: e.clientX, y: e.clientY }
  }
  const move = (b: SideBlock, area: SideArea, index: number, delta: -1 | 1) => {
    const list = onColumn(area)
    const next = index + delta
    if (next >= 0 && next < list.length) onChange(placeBlock(blocks, b.id, area, next))
    else if (delta === 1 && area === 'top') onChange(placeBlock(blocks, b.id, 'dock', 0))
    else if (delta === -1 && area === 'dock') onChange(placeBlock(blocks, b.id, 'top', onColumn('top').length))
  }
  // Where the drop line goes: before the n-th other row of the area.
  const lineBefore = (area: SideArea, id: SideBlockId) => {
    if (!drag || drop?.area !== area) return false
    const others = onColumn(area).filter((b) => b.id !== drag.id)
    return others[drop.index]?.id === id
  }
  const lineAtEnd = (area: SideArea) => {
    if (!drag || drop?.area !== area) return false
    return drop.index >= onColumn(area).filter((b) => b.id !== drag.id).length
  }

  return (
    <div ref={root} className={`rc-side-edit rc-fan-scroll ${drag ? 'is-dragging' : ''}`} role="region" aria-label={t('side.edit.title')}>
      <p className="rc-side-edit-help">{t('side.edit.help')}</p>
      {AREAS.map((area) => {
        const list = onColumn(area)
        return (
          <div key={area} className="rc-side-edit-area">
            <span className="rc-side-edit-label">{t(`side.edit.${area}`)}</span>
            <ul aria-label={t(`side.edit.${area}`)} data-drop-area={area} className={`rc-side-edit-list ${drag && drop?.area === area ? 'is-over' : ''}`}>
              {!list.length && <li className="rc-side-edit-empty">{t('side.edit.empty')}</li>}
              {list.map((b, i) => (
                <li
                  key={b.id}
                  aria-label={name(b.id)}
                  data-block={b.id}
                  onPointerDown={startPress(b.id)}
                  className={[
                    'rc-side-edit-item',
                    drag?.id === b.id ? 'is-dragging' : '',
                    lineBefore(area, b.id) ? 'drop-before' : '',
                    i === list.length - 1 && lineAtEnd(area) ? 'drop-after' : '',
                  ].join(' ')}
                >
                  <FeatherIcon name="grip-vertical" size={14} className="rc-side-edit-grip" />
                  <FeatherIcon name={BLOCK_ICONS[b.id]} size={14} />
                  <span className="rc-side-edit-name">{name(b.id)}</span>
                  <button
                    type="button"
                    className="rc-icon-btn"
                    aria-label={t('side.edit.up', { block: name(b.id) })}
                    title={t('side.edit.up', { block: name(b.id) })}
                    disabled={area === 'top' && i === 0}
                    onClick={() => move(b, area, i, -1)}
                  >
                    <FeatherIcon name="chevron-up" size={14} />
                  </button>
                  <button
                    type="button"
                    className="rc-icon-btn"
                    aria-label={t('side.edit.down', { block: name(b.id) })}
                    title={t('side.edit.down', { block: name(b.id) })}
                    disabled={area === 'dock' && i === list.length - 1}
                    onClick={() => move(b, area, i, 1)}
                  >
                    <FeatherIcon name="chevron-down" size={14} />
                  </button>
                  <button
                    type="button"
                    className="rc-icon-btn rc-side-edit-remove"
                    aria-label={t('side.edit.remove', { block: name(b.id) })}
                    title={t('side.edit.remove', { block: name(b.id) })}
                    onClick={() => onChange(removeBlock(blocks, b.id))}
                  >
                    <FeatherIcon name="minus-circle" size={15} />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )
      })}
      <div className="rc-side-edit-area">
        <span className="rc-side-edit-label">{t('side.edit.add')}</span>
        <ul aria-label={t('side.edit.add')} data-drop-area="add" className={`rc-side-edit-list rc-side-edit-list--add ${drag && drop?.area === 'add' ? 'is-over' : ''}`}>
          {!toAdd.length && <li className="rc-side-edit-empty">{drag ? t('side.edit.dropToRemove') : t('side.edit.allAdded')}</li>}
          {toAdd.map((b) => (
            <li key={b.id} aria-label={name(b.id)} data-block={b.id} onPointerDown={startPress(b.id)} className={`rc-side-edit-item rc-side-edit-item--add ${drag?.id === b.id ? 'is-dragging' : ''}`}>
              <FeatherIcon name={BLOCK_ICONS[b.id]} size={14} />
              <span className="rc-side-edit-name">
                {name(b.id)}
                <small>{t(`side.edit.about.${b.id}`)}</small>
              </span>
              <button type="button" className="rc-side-edit-addbtn" aria-label={t('side.edit.addOne', { block: name(b.id) })} title={t('side.edit.addOne', { block: name(b.id) })} onClick={() => onChange(addBlock(blocks, b.id))}>
                <FeatherIcon name="plus" size={13} />
                {t('side.edit.addShort')}
              </button>
            </li>
          ))}
        </ul>
      </div>
      {drag && (
        <div className="rc-side-edit-ghost" style={{ left: inApp(drag.x + 12), top: inApp(drag.y - 14) }} aria-hidden="true">
          <FeatherIcon name={BLOCK_ICONS[drag.id]} size={13} />
          {name(drag.id)}
        </div>
      )}
      <button type="button" className="rc-side-edit-reset" onClick={() => onChange(null)}>
        <FeatherIcon name="rotate-ccw" size={12} />
        {t('side.edit.reset')}
      </button>
    </div>
  )
}
